import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useDownloadSalarySalaries,
  useGetSalarySalaries,
} from "src/api/salary/salaries";
import { downloadAsXLS } from "src/utils";
import DateRangeInput from "src/shared/components/date-range";
import { NoData } from "src/shared/components/tables/no-data";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import SalaryModal from "./add-edit-salary";
import SalariesTable from "./table";

const SalariesPage = () => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get("month") ?? "";
  const salaryMonthFilter = /^\d{4}-\d{2}$/.test(monthParam) ? monthParam : undefined;

  const [pageNumber, setPageNumber] = useState(0);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });
  const [appliedDateRange, setAppliedDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!dateRange.startDate && !dateRange.endDate) {
      setAppliedDateRange({ startDate: "", endDate: "" });
      setPageNumber(0);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const isYmd = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);
  const paymentDateFrom =
    appliedDateRange.startDate && isYmd(appliedDateRange.startDate)
      ? appliedDateRange.startDate
      : undefined;
  const paymentDateTo =
    appliedDateRange.endDate && isYmd(appliedDateRange.endDate)
      ? appliedDateRange.endDate
      : undefined;

  const filters = {
    salary_month: salaryMonthFilter,
    payment_date_from: paymentDateFrom,
    payment_date_to: paymentDateTo,
  };

  const { data, isLoading, refetch } = useGetSalarySalaries({
    page: pageNumber + 1,
    ...filters,
  });

  const {
    data: downloadedData,
    refetch: refetchDownload,
    isFetching: isFetchingDownload,
    status: downloadStatus,
  } = useDownloadSalarySalaries(filters);

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const empty = !isLoading && rows.length === 0;

  useEffect(() => {
    if (downloadStatus === "success" && !!downloadedData) {
      const utf8CSVData = `\uFEFF${downloadedData}`;
      downloadAsXLS(utf8CSVData, "salaries");
    }
  }, [isFetchingDownload]);

  return (
    <Stack gap={2} sx={{ width: "100%", minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
        sx={{ width: "100%", minWidth: 0 }}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography
            fontWeight={600}
            lineHeight="19.2px"
            color="#475467"
            textTransform="uppercase"
          >
            {t("salaries")}
          </Typography>
          {salaryMonthFilter ? (
            <Chip
              size="small"
              label={t("filteredBySalaryMonth", { month: salaryMonthFilter })}
              onDelete={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("month");
                setSearchParams(next, { replace: true });
                setPageNumber(0);
              }}
              sx={{ fontWeight: 500 }}
            />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1}
          sx={{
            width: { xs: "100%", md: "auto" },
            minWidth: 0,
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          <DateRangeInput
            startDate={dateRange.startDate ? moment(dateRange.startDate) : null}
            startDateId="salary_salaries_start_date"
            endDate={dateRange.endDate ? moment(dateRange.endDate) : null}
            endDateId="salary_salaries_end_date"
            onDatesChange={(arg: {
              startDate: moment.Moment | null;
              endDate: moment.Moment | null;
            }) =>
              setDateRange({
                startDate: arg.startDate?.format("yyyy-MM-DD") ?? "",
                endDate: arg.endDate?.format("yyyy-MM-DD") ?? "",
              })
            }
            showClearDates
          />
          <Button
            color="tertiary"
            variant="outlined"
            endIcon={<img src={FilterIcon} height="16px" alt="" />}
            onClick={() => {
              let { startDate, endDate } = dateRange;
              if (startDate && endDate && startDate > endDate) {
                [startDate, endDate] = [endDate, startDate];
              }
              setAppliedDateRange({ startDate, endDate });
              setPageNumber(0);
            }}
          >
            {generalT("filter")}
          </Button>
          <LoadingButton
            color="tertiary"
            variant="outlined"
            endIcon={<img src={DownloadIcon} alt="" />}
            loading={isFetchingDownload}
            onClick={() => refetchDownload()}
            disabled={isLoading || total === 0}
          >
            {generalT("download")}
          </LoadingButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            {t("addNewSalary")}
          </Button>
        </Stack>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          overflowX: "auto",
        }}
      >
        {isLoading ? (
          <Box p={2}>
            <TableLoadingSkeleton rowsLength={8} cellsLength={8} />
          </Box>
        ) : empty ? (
          <Box p={2}>
            <Stack alignItems="center" gap={2} py={2}>
              <NoData />
              <Button variant="contained" onClick={() => setCreateOpen(true)}>
                {t("createSalary")}
              </Button>
            </Stack>
          </Box>
        ) : (
          <SalariesTable
            rows={rows}
            total={total}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            refetch={refetch}
          />
        )}
      </Paper>

      <SalaryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          refetch();
        }}
      />
    </Stack>
  );
};

export default SalariesPage;

